/* eslint-disable no-console */
import { Web3Provider } from '@ethersproject/providers';
import { useWeb3React as useWeb3ReactCore } from '@web3-react/core';
import { Web3ReactContextInterface } from '@web3-react/core/dist/types';
import { useEffect } from 'react';
import { injected } from '../connectors';
import { setSignerOrProvider } from '../utils';
import { isWeb } from '@r3l/common';

declare global {
  interface Window {
    ethereum: any;
  }
}

export function useWeb3React(): Web3ReactContextInterface<Web3Provider> & {
  metamask: boolean;
} {
  const context = useWeb3ReactCore<Web3Provider>();
  // const contextNetwork = useWeb3ReactCore<Web3Provider>(NetworkContextName);
  // return context.active ? context : contextNetwork;
  const metamask = isWeb && !!window.ethereum;

  return { ...context, metamask };
}

export function useWeb3(): void {
  const { activate, library, active } = useWeb3ReactCore();

  const haveMM = isWeb && !!window.ethereum;
  if (haveMM) window.ethereum.autoRefreshOnNetworkChange = false;

  useEffect(() => {
    haveMM && activate(injected);
  }, [haveMM, activate]);

  useEffect(() => {
    library && active && setSignerOrProvider(library.getUncheckedSigner());
  }, [library, active]);

  useInactiveListener();
}

/**
 * Use for network and injected - logs user in
 * and out after checking what network theyre on
 */
export function useInactiveListener(suppress = false) {
  const { active, error, activate } = useWeb3ReactCore(); // specifically using useWeb3React because of what this hook does

  useEffect(() => {
    if (!isWeb) return;
    const { ethereum } = window;

    if (ethereum && ethereum.on && !active && !error && !suppress) {
      const handleChainChanged = () => {
        // eat errors
        activate(injected, undefined, true).catch((error) => {
          console.error('Failed to activate after chain changed', error);
        });
      };

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length > 0) {
          // eat errors
          activate(injected, undefined, true).catch((error) => {
            console.error('Failed to activate after accounts changed', error);
          });
        }
      };

      const handleNetworkChanged = () => {
        activate(injected, undefined, true).catch((error) => {
          console.error('Failed to activate after networks changed', error);
        });
      };

      ethereum.on('chainChanged', handleChainChanged);
      ethereum.on('accountsChanged', handleAccountsChanged);
      ethereum.on('networkChanged', handleNetworkChanged);

      return () => {
        if (ethereum.removeListener) {
          ethereum.removeListener('chainChanged', handleChainChanged);
          ethereum.removeListener('accountsChanged', handleAccountsChanged);
          ethereum.removeListener('networkChanged', handleNetworkChanged);
        }
      };
    }
    return undefined;
  }, [active, error, suppress, activate]);
}
